import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { fetchEmailTemplates, fetchEmailTemplateById, deleteEmailTemplate } from '../../../controllers/emailTemplateController';
import EmailTemplateFormDialog from './EmailTemplateFormDialog';
import EmailTemplatePreviewDialog from './EmailTemplatePreviewDialog';

const defaultPagination = {
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 1,
};

const EmailTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [meta, setMeta] = useState(defaultPagination);
  const [filters, setFilters] = useState({
    search: '',
    isActive: '',
    isSystemTemplate: '',
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteState, setDeleteState] = useState({ open: false, id: null, name: '' });
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        search: filters.search || undefined,
        isActive: filters.isActive === '' ? undefined : filters.isActive === 'true',
        isSystemTemplate: filters.isSystemTemplate === '' ? undefined : filters.isSystemTemplate === 'true',
      };
      const { data, meta: metaData } = await fetchEmailTemplates(params);
      setTemplates(Array.isArray(data) ? data : []);
      setMeta({
        pageNumber: metaData?.pageNumber ?? params.pageNumber,
        pageSize: metaData?.pageSize ?? params.pageSize,
        totalCount: metaData?.totalCount ?? (Array.isArray(data) ? data.length : 0),
        totalPages: metaData?.totalPages ?? 1,
      });
    } catch (err) {
      setError(err?.message || 'E-posta şablonları alınamadı');
      setTemplates([]);
      setMeta(defaultPagination);
    } finally {
      setLoading(false);
    }
  }, [filters.isActive, filters.isSystemTemplate, filters.search, page, rowsPerPage]);

  useEffect(() => {
    let mounted = true;
    loadTemplates();
    return () => {
      mounted = false;
    };
  }, [loadTemplates]);

  const handleRefresh = () => {
    loadTemplates();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCreateDialog = () => {
    setFormMode('create');
    setActiveTemplate(null);
    setFormOpen(true);
  };

  const openEditDialog = async (id) => {
    setLoadingDetail(true);
    try {
      const detail = await fetchEmailTemplateById(id);
      setActiveTemplate(detail);
      setFormMode('edit');
      setFormOpen(true);
    } catch (err) {
      setError(err?.message || 'Şablon detayları alınamadı');
    } finally {
      setLoadingDetail(false);
    }
  };

  const openPreviewDialog = async (id) => {
    setLoadingDetail(true);
    try {
      const detail = await fetchEmailTemplateById(id);
      setActiveTemplate(detail);
      setPreviewOpen(true);
    } catch (err) {
      setError(err?.message || 'Şablon önizlemesi yüklenemedi');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.id) return;
    try {
      await deleteEmailTemplate(deleteState.id);
      setDeleteState({ open: false, id: null, name: '' });
      await loadTemplates();
    } catch (err) {
      setError(err?.message || 'Şablon silinemedi');
    }
  };

  const filterOptions = useMemo(
    () => ({
      isActive: [
        { label: 'Tümü', value: '' },
        { label: 'Aktif', value: 'true' },
        { label: 'Pasif', value: 'false' },
      ],
      isSystemTemplate: [
        { label: 'Tümü', value: '' },
        { label: 'Sistem Şablonu', value: 'true' },
        { label: 'Özel Şablon', value: 'false' },
      ],
    }),
    []
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
          <TextField
            size="small"
            label="Ara"
            placeholder="Şablon adı veya konu"
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            size="small"
            select
            label="Durum"
            value={filters.isActive}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, isActive: e.target.value }));
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            {filterOptions.isActive.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Tür"
            value={filters.isSystemTemplate}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, isSystemTemplate: e.target.value }));
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            {filterOptions.isSystemTemplate.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Yeni Şablon
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ad</TableCell>
                    <TableCell>Konu</TableCell>
                    <TableCell>Tür</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Oluşturulma</TableCell>
                    <TableCell align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                          Henüz şablon bulunmuyor. Yeni şablon oluşturmayı deneyin.
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 600 }}>{template.name}</Typography>
                            {template.description && (
                              <Typography variant="body2" color="text.secondary">
                                {template.description}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{template.subject}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={template.isSystemTemplate ? 'Sistem' : 'Özel'}
                            color={template.isSystemTemplate ? 'primary' : 'default'}
                            variant={template.isSystemTemplate ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={template.isActive ? 'Aktif' : 'Pasif'}
                            color={template.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {template.createdAt ? new Date(template.createdAt).toLocaleString('tr-TR') : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Önizle">
                            <span>
                              <IconButton onClick={() => openPreviewDialog(template.id)} disabled={loadingDetail}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <span>
                              <IconButton onClick={() => openEditDialog(template.id)} disabled={loadingDetail}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <span>
                              <IconButton
                                color="error"
                                onClick={() =>
                                  setDeleteState({ open: true, id: template.id, name: template.name })
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={meta.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      <EmailTemplateFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        mode={formMode}
        template={formMode === 'edit' ? activeTemplate : null}
        onSaved={() => {
          setFormOpen(false);
          loadTemplates();
        }}
      />

      <EmailTemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} template={previewOpen ? activeTemplate : null} />

      <Dialog
        open={deleteState.open}
        onClose={() => setDeleteState({ open: false, id: null, name: '' })}
      >
        <DialogTitle>Şablonu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteState.name
              ? `"${deleteState.name}" adlı şablonu silmek istediğinize emin misiniz?`
              : 'Bu şablonu silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteState({ open: false, id: null, name: '' })}>İptal</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EmailTemplatesTab;


